import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.authForgotPassword({ email });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verzoek mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/forgot-password"
        title="Wachtwoord vergeten"
        description="Wachtwoord vergeten bij CalqTool™? Vraag een beveiligde resetlink per e-mail aan. Geen account? Registreer via de inlogpagina."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Wachtwoord vergeten' }]} />
        <div className="mx-auto max-w-md rounded-lg border border-border bg-surface-card p-8">
          <h1 className="mb-1 text-2xl font-bold text-text">Wachtwoord vergeten</h1>
          <p className="mb-6 text-sm text-text-muted">
            Vul je e-mailadres in. Als het bij ons bekend is, sturen we een link om een nieuw wachtwoord in te stellen
            (ongeveer 1 uur geldig).
          </p>

          {done ? (
            <div
              className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-text"
              role="status"
              aria-live="polite"
            >
              <p className="font-medium">Verzoek ontvangen</p>
              <p className="mt-2 text-text-muted">
                Als dit e-mailadres bij ons bekend is, ontvang je zo een e-mail (controleer ook spam). Daarna kun je via
                de link een nieuw wachtwoord kiezen.
              </p>
              <p className="mt-4">
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Terug naar inloggen
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="mb-1 block text-sm font-medium text-text">
                  E-mailadres
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  autoComplete="email"
                />
              </div>
              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-primary py-3 font-medium text-white transition-all hover:bg-primary-alt disabled:opacity-60"
              >
                {loading ? 'Bezig…' : 'Resetlink aanvragen'}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-text-muted">
            <Link to="/login" className="text-primary hover:underline">
              ← Terug naar inloggen
            </Link>
          </p>
        </div>
      </AnimatedSection>
    </div>
  );
}
